import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/utils/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Scissors, ArrowLeft, CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5C3A2E] rounded-2xl mb-5">
            <Scissors className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-heading text-4xl text-[#5C3A2E] font-semibold tracking-tight">Reset Password</h1>
          <p className="text-[#2B2B2B]/60 mt-2 font-body text-base">We'll send you a reset link</p>
        </div>

        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] p-8 animate-fade-in-up animate-stagger-2">
          {sent ? (
            <div className="text-center py-4">
              <CheckCircle className="w-10 h-10 text-[#C6A969] mx-auto mb-3" />
              <h3 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-2">Check your email</h3>
              <p className="text-[#2B2B2B]/50 text-sm">If an account exists with that email, you'll receive a password reset link shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div data-testid="forgot-error" className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-100">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">Email</label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full rounded-xl border-[#EADBC8] bg-white px-4 py-3 text-[#2B2B2B] focus:ring-2 focus:ring-[#C6A969] focus:border-transparent"
                  required
                />
              </div>
              <Button data-testid="forgot-submit-button" type="submit" disabled={submitting} className="w-full bg-[#5C3A2E] text-white rounded-full py-3 hover:bg-[#5C3A2E]/90 h-12">
                {submitting ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          )}
          <p className="text-center mt-6 text-sm text-[#2B2B2B]/60">
            <Link to="/login" className="inline-flex items-center gap-1 text-[#5C3A2E] font-medium hover:text-[#C6A969] transition-colors">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
